/**
 * <s-engraving> — Optional ring engraving on the product form.
 *
 * A checkbox reveals the engraving fields. While enabled, the text and timing
 * inputs submit as line-item properties on the ring, and a hidden
 * input[data-addon-variant] tells <s-product-form> to add the engraving service
 * variant as a second cart line. While disabled every field is disabled, so
 * nothing extra reaches the cart.
 *
 * The product-form partial is replaced on each variant change, so the element
 * keeps its state per product in module scope and restores it on reconnect;
 * changing the ring size never wipes what the shopper typed.
 *
 * Required child markup:
 *   input[type=checkbox][data-engraving-toggle]
 *   [data-engraving-fields]           — Revealed while enabled.
 *   input[data-engraving-text]        — Engraving text (maxlength honoured).
 *   input[type=radio][data-engraving-timing]
 *   input[type=hidden][data-addon-variant]
 *   [data-engraving-preview]          — Shows the text, or the sample when empty.
 *   [data-engraving-count]            — Characters used.
 *   button[data-engraving-symbol]     — Inserts its value at the caret.
 *
 * Element dataset:
 *   data-product-id — State key.
 *   data-sample     — Preview text shown while the input is empty.
 */
const engravingState = new Map();

class SEngraving extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#restore();
    this.#sync();

    const frame = this.querySelector('[data-engraving-preview-frame]');
    if (frame && 'ResizeObserver' in window) {
      let lastWidth = 0;
      const observer = new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (width === lastWidth) return;
        lastWidth = width;
        this.#fitPreview();
      });
      observer.observe(frame);
      this.#ac.signal.addEventListener('abort', () => observer.disconnect());
    }

    this.#toggle()?.addEventListener('change', () => {
      this.#sync();
      this.#save();
      if (this.#toggle().checked) this.#text()?.focus({ preventScroll: true });
    }, opts);

    this.#text()?.addEventListener('input', () => {
      this.#render();
      this.#save();
    }, opts);

    this.querySelectorAll('[data-engraving-timing]').forEach((radio) => {
      radio.addEventListener('change', () => this.#save(), opts);
    });

    this.querySelectorAll('[data-engraving-symbol]').forEach((button) => {
      button.addEventListener('click', () => this.#insert(button.dataset.engravingSymbol || ''), opts);
    });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #toggle() {
    return this.querySelector('[data-engraving-toggle]');
  }

  #text() {
    return this.querySelector('[data-engraving-text]');
  }

  #key() {
    return this.dataset.productId || '';
  }

  #save() {
    const timing = this.querySelector('[data-engraving-timing]:checked');
    engravingState.set(this.#key(), {
      enabled: Boolean(this.#toggle()?.checked),
      text: this.#text()?.value || '',
      timing: timing ? timing.value : '',
    });
  }

  #restore() {
    const state = engravingState.get(this.#key());
    if (!state) return;
    const toggle = this.#toggle();
    const text = this.#text();
    if (toggle) toggle.checked = state.enabled;
    if (text) text.value = state.text;
    this.querySelectorAll('[data-engraving-timing]').forEach((radio) => {
      radio.checked = radio.value === state.timing;
    });
  }

  #sync() {
    const enabled = Boolean(this.#toggle()?.checked);
    const fields = this.querySelector('[data-engraving-fields]');
    if (fields) fields.hidden = !enabled;
    this.toggleAttribute('data-enabled', enabled);

    fields?.querySelectorAll('input, button').forEach((control) => {
      control.disabled = !enabled;
    });
    const addon = this.querySelector('[data-addon-variant]');
    if (addon) addon.disabled = !enabled;

    const text = this.#text();
    if (text) text.required = enabled;

    this.#render();
  }

  #render() {
    const text = this.#text();
    const value = text?.value || '';
    const preview = this.querySelector('[data-engraving-preview]');
    if (preview) {
      preview.textContent = value.trim() || this.dataset.sample || '';
      preview.toggleAttribute('data-empty', !value.trim());
    }
    this.#fitPreview();
    const count = this.querySelector('[data-engraving-count]');
    if (count) count.textContent = String(value.length);

    const addon = this.querySelector('[data-addon-variant]');
    if (addon) addon.value = value.trim();
  }

  /**
   * Shrinks the preview text so long engravings stay on one line inside the
   * panel instead of widening it. Below the minimum size the text wraps.
   */
  #fitPreview() {
    const preview = this.querySelector('[data-engraving-preview]');
    const frame = this.querySelector('[data-engraving-preview-frame]');
    if (!preview || !frame || frame.closest('[hidden]')) return;

    const maxSize = 24;
    const minSize = 13;
    preview.style.whiteSpace = 'nowrap';
    preview.style.overflowWrap = '';
    preview.style.fontSize = `${maxSize}px`;

    const available = frame.clientWidth;
    if (!available) return;

    let size = maxSize;
    while (preview.scrollWidth > available && size > minSize) {
      size -= 1;
      preview.style.fontSize = `${size}px`;
    }
    if (preview.scrollWidth > available) {
      preview.style.whiteSpace = 'normal';
      preview.style.overflowWrap = 'anywhere';
    }
  }

  #insert(symbol) {
    const text = this.#text();
    if (!text || !symbol || text.disabled) return;
    const max = text.maxLength > 0 ? text.maxLength : Infinity;
    if (text.value.length + symbol.length > max) return;

    const start = text.selectionStart ?? text.value.length;
    const end = text.selectionEnd ?? text.value.length;
    text.setRangeText(symbol, start, end, 'end');
    text.focus();
    text.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

if (!customElements.get('s-engraving')) {
  customElements.define('s-engraving', SEngraving);
}
